import React from 'react';
import Button from './Button.jsx';
import './EmptyState.css';

/**
 * EmptyState component for no-data scenarios
 * Based on ArbiWin design system
 *
 * @param {Object} props - EmptyState properties
 * @param {React.ReactNode} props.icon - Custom icon component
 * @param {string} props.title - Empty state title
 * @param {string} props.description - Empty state description
 * @param {React.ReactNode} props.actions - Action buttons or components
 * @param {string} props.variant - Empty state variant: 'default', 'search', 'error', 'maintenance'
 * @param {string} props.size - Empty state size: 'small', 'medium', 'large'
 * @param {React.ReactNode} props.illustration - Custom illustration component
 * @param {string} props.className - Additional CSS classes
 * @param {string} props.testId - Test ID for testing
 * @returns {JSX.Element} EmptyState component
 */
const EmptyState = function EmptyState({
  icon,
  title,
  description,
  actions,
  variant = 'default',
  size = 'medium',
  illustration,
  className = '',
  testId
}) {

  const emptyStateClasses = [
    'arbiwin-empty-state',
    'arbiwin-empty-state--' + variant,
    'arbiwin-empty-state--' + size,
    className
  ].filter(Boolean).join(' ');

  const getDefaultIcon = function() {
    const icons = {
      default: (
        <svg className="arbiwin-empty-state__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <circle cx="12" cy="12" r="10" />
          <path d="M12 6v6l4 2" />
        </svg>
      ),
      search: (
        <svg className="arbiwin-empty-state__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <circle cx="11" cy="11" r="8" />
          <path d="M21 21l-4.35-4.35" />
          <path d="M11 8a3 3 0 100 6 3 3 0 000-6z" />
        </svg>
      ),
      error: (
        <svg className="arbiwin-empty-state__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <circle cx="12" cy="12" r="10" />
          <line x1="15" y1="9" x2="9" y2="15" />
          <line x1="9" y1="9" x2="15" y2="15" />
        </svg>
      ),
      maintenance: (
        <svg className="arbiwin-empty-state__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <path d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z" />
        </svg>
      )
    };
    return icons[variant] || icons.default;
  };

  const getDefaultContent = function() {
    const content = {
      default: {
        title: 'Няма налични данни',
        description: 'В момента няма информация за показване.'
      },
      search: {
        title: 'Няма резултати',
        description: 'Не намерихме резултати, които да отговарят на вашето търсене.'
      },
      error: {
        title: 'Възникна грешка',
        description: 'Нещо се обърка при зареждането на данните.'
      },
      maintenance: {
        title: 'Временно недостъпно',
        description: 'Услугата временно не е достъпна поради поддръжка.'
      }
    };
    return content[variant] || content.default;
  };

  const defaultContent = getDefaultContent();
  const displayTitle = title || defaultContent.title;
  const displayDescription = description || defaultContent.description;

  return (
    <div
      className={emptyStateClasses}
      data-testid={testId || 'arbiwin-empty-state'}
    >
      {illustration ? (
        <div className="arbiwin-empty-state__illustration">
          {illustration}
        </div>
      ) : (
        <div className="arbiwin-empty-state__icon-wrapper">
          {icon || getDefaultIcon()}
        </div>
      )}

      <div className="arbiwin-empty-state__content">
        <h3 className="arbiwin-empty-state__title">
          {displayTitle}
        </h3>

        {displayDescription && (
          <p className="arbiwin-empty-state__description">
            {displayDescription}
          </p>
        )}
      </div>

      {actions && (
        <div className="arbiwin-empty-state__actions">
          {actions}
        </div>
      )}
    </div>
  );
};

/**
 * NoDataState component - Pre-configured for no data scenarios
 *
 * @param {Object} props - NoDataState properties
 * @param {string} props.title - Custom title
 * @param {string} props.description - Custom description
 * @param {Function} props.onRefresh - Refresh handler
 * @param {string} props.refreshLabel - Refresh button label
 * @param {string} props.className - Additional CSS classes
 * @returns {JSX.Element} NoDataState component
 */
const NoDataState = function NoDataState({
  title = 'Няма данни',
  description = 'В момента няма налична информация за показване.',
  onRefresh,
  refreshLabel = 'Опресняване',
  className = ''
}) {
  return (
    <EmptyState
      variant="default"
      title={title}
      description={description}
      actions={onRefresh && (
        <Button
          variant="primary"
          onClick={onRefresh}
        >
          {refreshLabel}
        </Button>
      )}
      className={'arbiwin-no-data-state ' + className}
    />
  );
};

/**
 * SearchEmptyState component - Pre-configured for search results
 *
 * @param {Object} props - SearchEmptyState properties
 * @param {string} props.searchTerm - The search term that returned no results
 * @param {Function} props.onClearSearch - Clear search handler
 * @param {Function} props.onTryAgain - Try again handler
 * @param {string} props.className - Additional CSS classes
 * @returns {JSX.Element} SearchEmptyState component
 */
const SearchEmptyState = function SearchEmptyState({
  searchTerm,
  onClearSearch,
  onTryAgain,
  className = ''
}) {

  const description = searchTerm
    ? `Не намерихме резултати за "${searchTerm}". Опитайте с други ключови думи.`
    : 'Не намерихме резултати, които да отговарят на вашето търсене.';

  const actions = (
    <div className="arbiwin-search-empty-actions">
      {onClearSearch && (
        <Button
          variant="secondary"
          onClick={onClearSearch}
        >
          Изчисти търсенето
        </Button>
      )}
      {onTryAgain && (
        <Button
          variant="primary"
          onClick={onTryAgain}
        >
          Опитай отново
        </Button>
      )}
    </div>
  );

  return (
    <EmptyState
      variant="search"
      title="Няма резултати от търсенето"
      description={description}
      actions={actions}
      className={'arbiwin-search-empty-state ' + className}
    />
  );
};

/**
 * ErrorState component - Pre-configured for error scenarios
 *
 * @param {Object} props - ErrorState properties
 * @param {string} props.title - Error title
 * @param {string} props.description - Error description
 * @param {Function} props.onRetry - Retry handler
 * @param {Function} props.onGoBack - Go back handler
 * @param {string} props.retryLabel - Retry button label
 * @param {string} props.backLabel - Back button label
 * @param {string} props.className - Additional CSS classes
 * @returns {JSX.Element} ErrorState component
 */
const ErrorState = function ErrorState({
  title = 'Възникна грешка',
  description = 'Нещо се обърка при зареждането на данните. Моля, опитайте отново.',
  onRetry,
  onGoBack,
  retryLabel = 'Опитай отново',
  backLabel = 'Назад',
  className = ''
}) {

  const actions = (
    <div className="arbiwin-error-actions">
      {onGoBack && (
        <Button
          variant="secondary"
          onClick={onGoBack}
        >
          {backLabel}
        </Button>
      )}
      {onRetry && (
        <Button
          variant="primary"
          onClick={onRetry}
        >
          {retryLabel}
        </Button>
      )}
    </div>
  );

  return (
    <EmptyState
      variant="error"
      title={title}
      description={description}
      actions={actions}
      className={'arbiwin-error-state ' + className}
    />
  );
};

/**
 * MaintenanceState component - Pre-configured for maintenance scenarios
 *
 * @param {Object} props - MaintenanceState properties
 * @param {string} props.title - Maintenance title
 * @param {string} props.description - Maintenance description
 * @param {Date} props.expectedResolution - Expected resolution time
 * @param {Function} props.onCheckAgain - Check again handler
 * @param {string} props.className - Additional CSS classes
 * @returns {JSX.Element} MaintenanceState component
 */
const MaintenanceState = function MaintenanceState({
  title = 'Временно недостъпно',
  description = 'Услугата временно не е достъпна поради планирана поддръжка.',
  expectedResolution,
  onCheckAgain,
  className = ''
}) {

  let fullDescription = description;
  if (expectedResolution) {
    const resolutionTime = expectedResolution.toLocaleString('bg-BG');
    fullDescription += ` Очакваме да възстановим услугата до ${resolutionTime}.`;
  }

  return (
    <EmptyState
      variant="maintenance"
      title={title}
      description={fullDescription}
      actions={onCheckAgain && (
        <Button
          variant="primary"
          onClick={onCheckAgain}
        >
          Провери отново
        </Button>
      )}
      className={'arbiwin-maintenance-state ' + className}
    />
  );
};

/**
 * SurebetEmptyState component - Pre-configured for surebet scenarios
 *
 * @param {Object} props - SurebetEmptyState properties
 * @param {Function} props.onRefresh - Refresh handler
 * @param {Function} props.onClearFilters - Clear filters handler
 * @param {boolean} props.hasFilters - Whether filters are applied
 * @param {string} props.className - Additional CSS classes
 * @returns {JSX.Element} SurebetEmptyState component
 */
const SurebetEmptyState = function SurebetEmptyState({
  onRefresh,
  onClearFilters,
  hasFilters = false,
  className = ''
}) {

  const title = hasFilters
    ? 'Няма резултати с текущите филтри'
    : 'Няма арбитражни възможности';

  const description = hasFilters
    ? 'Не намерихме арбитражни възможности, които да отговарят на избраните филтри. Опитайте да промените критериите за търсене.'
    : 'В момента няма налични арбитражни възможности. Данните се обновяват автоматично на всеки 10 минути.';

  const actions = (
    <div className="arbiwin-surebet-empty-actions">
      {hasFilters && onClearFilters && (
        <Button
          variant="secondary"
          onClick={onClearFilters}
        >
          Премахни филтрите
        </Button>
      )}
      {onRefresh && (
        <Button
          variant="primary"
          onClick={onRefresh}
        >
          Опресни данните
        </Button>
      )}
    </div>
  );

  const icon = (
    <svg className="arbiwin-empty-state__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
      <path d="M9 11H5a2 2 0 000 4h4" />
      <path d="M15 13h4a2 2 0 000-4h-4" />
      <path d="M9 13h6" />
      <circle cx="12" cy="12" r="2" />
    </svg>
  );

  return (
    <EmptyState
      icon={icon}
      title={title}
      description={description}
      actions={actions}
      size="large"
      className={'arbiwin-surebet-empty-state ' + className}
    />
  );
};

EmptyState.displayName = 'EmptyState';
NoDataState.displayName = 'NoDataState';
SearchEmptyState.displayName = 'SearchEmptyState';
ErrorState.displayName = 'ErrorState';
MaintenanceState.displayName = 'MaintenanceState';
SurebetEmptyState.displayName = 'SurebetEmptyState';

export default EmptyState;
export { NoDataState, SearchEmptyState, ErrorState, MaintenanceState, SurebetEmptyState };
